import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Dropdown } from 'react-bootstrap'

import { getIntegrationDisplayName, INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../common/integrations'

import { useAppSelector } from '../../store/hooks'
import {
  selectContactChange,
  selectIsLoadingContact,
  selectIsLoadingContactChange,
  selectIsLoadingRerun,
  selectIsLoadingSave,
  selectSegmentLoadingStates,
  selectSelectedKeys,
} from '../../store/meetingSummary/contactUpdate'

import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'

import { useExportConfirmation } from '../../components/ConfirmationDialog/useExportConfirmation'
import { Button } from '../../components/Button/Button'

import { User } from '../../models/User'

import { filterIgnoreFieldsFromKeys, getPreviousValue, hasValueChanged, SEARCH_KEY_MAP, DEFAULT_SEARCH_KEY, SEPARATOR } from './utils'
import { ExternalContactUpdateProps, useRerunContactUpdate, useSaveContactUpdate } from './hooks'
import { ContactUpdateFormValues } from './models'

interface UpdateExternalContactFooterProps {
  serviceId: IntegrationServiceId
  user: User
  updateProps: ExternalContactUpdateProps
  onCancel: () => void
  onSave?: () => void
  onRerun?: () => void
  onNext?: () => void
  onPrevious?: () => void
  canNext?: boolean
  canPrevious?: boolean
  onBeforeSave?: () => void
}

export const UpdateExternalContactModalFooter: React.FC<UpdateExternalContactFooterProps> = ({
  serviceId,
  user,
  updateProps,
  onCancel,
  onSave,
  onRerun,
  onNext,
  onPrevious,
  canNext,
  canPrevious,
  onBeforeSave,
}) => {
  const { id: contactId } = user

  const isLoadingContact = useAppSelector(selectIsLoadingContact)
  const contactChange = useAppSelector(selectContactChange)
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const isLoadingSave = useAppSelector(selectIsLoadingSave)
  const isLoadingRerun = useAppSelector(selectIsLoadingRerun)
  const segmentLoadingStates = useAppSelector(selectSegmentLoadingStates)

  const isLoadingSegmentedContactChange = segmentLoadingStates ? Object.values(segmentLoadingStates).some((state) => state === true) : false

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()

  const { handleSubmit, formState, setValue, watch } = useFormContext<ContactUpdateFormValues>()

  const formContactValues = watch('contactValues')

  const selectedKeysCount = useMemo(() => {
    const keys = filterIgnoreFieldsFromKeys(serviceId, selectedKeys)
    if (!contactChange) {
      return keys.length
    }

    const searchKey = SEARCH_KEY_MAP[serviceId] ?? DEFAULT_SEARCH_KEY
    const currentContactValues = formContactValues ?? contactChange.contactValues

    return keys.filter((key) => {
      const parts = key.split(SEPARATOR)
      const valueParts = parts.slice(1)

      const previousValue = getPreviousValue(currentContactValues, contactChange.previousContactValues, valueParts, searchKey)
      const currentValue = valueParts.reduce((acc, part) => (acc ? acc[part] : undefined), currentContactValues)

      return hasValueChanged(previousValue, currentValue)
    }).length
  }, [serviceId, selectedKeys, contactChange, formContactValues])

  const isSaveDisabled = useMemo(
    () =>
      isLoadingContact ||
      isLoadingContactChange ||
      accountIsLoading ||
      !contactChange ||
      !selectedKeysCount ||
      !!Object.keys(formState.errors).length ||
      isLoadingSegmentedContactChange,
    [contactChange, formState.errors, isLoadingContact, isLoadingContactChange, selectedKeysCount, accountIsLoading, isLoadingSegmentedContactChange],
  )

  const hideLeftSide = isLoadingContact || isLoadingContactChange || accountIsLoading

  const isSegmented = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId)

  const { rerunContactUpdate } = useRerunContactUpdate(serviceId, user, updateProps, onRerun)
  const { saveContactUpdate } = useSaveContactUpdate({ serviceId, contactId, user, updateProps, onSave })

  const handleSave = async (data: ContactUpdateFormValues) => {
    onBeforeSave?.()
    await runMutationWithConfirmation(async () => {
      await saveContactUpdate(data)
    })
  }

  const handleRerun = async () => {
    await rerunContactUpdate()
  }

  const handleResetAll = useCallback(() => {
    if (!contactChange) {
      return
    }
    setValue('contactValues', contactChange.contactValues, { shouldDirty: true, shouldValidate: true })
  }, [contactChange, setValue])

  return (
    <div className='d-flex align-items-center justify-content-between w-100'>
      <div className='d-flex flex-column'>
        {hideLeftSide ? null : (
          <>
            <span className='label-small fw-normal text-muted'>
              {getIntegrationDisplayName(serviceId)} data may have changed since Zocks retrieved it. Rerun to see the latest.
            </span>
            <div className='d-flex align-items-center zocks-gap-0.25'>
              {contactChange?.updatedAt ? (
                <span className='label-small fw-normal text-muted'>Last generated on {formatDate(contactChange?.updatedAt)}</span>
              ) : (
                <span className='label-small fw-normal text-muted'>No previous contact change</span>
              )}
              <Button
                className='p-0 label-small fw-bold text-decoration-none zocks-text-gray-900 border-0 ms-1 bg-transparent'
                onClick={handleRerun}
                disabled={isLoadingContact || isLoadingContactChange || isLoadingSave || isLoadingSegmentedContactChange}
                loading={isLoadingRerun}
              >
                Rerun
              </Button>
            </div>
          </>
        )}
      </div>

      <div className='d-flex align-items-stretch zocks-gap-0.5'>
        <Dropdown align='end' className='d-flex'>
          <Dropdown.Toggle
            variant='link'
            className='button-medium p-0 border-0 zocks-text-secondary d-flex align-items-center justify-content-center h-100'
            id='dropdown-footer-actions'
            bsPrefix='btn'
          >
            <i className='bi bi-three-dots-vertical fs-5' />
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item onClick={handleResetAll} disabled={!contactChange}>
              <i className='bi bi-arrow-clockwise me-2' />
              Reset all
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Button className='button-medium' variant='secondary' onClick={onCancel}>
          Cancel
        </Button>
        <Button className='button-medium' variant='primary' onClick={handleSubmit(handleSave)} disabled={isSaveDisabled} loading={isLoadingSave}>
          {!isSaveDisabled ? `Update All (${selectedKeysCount})` : 'Update All'}
        </Button>

        {onNext && onPrevious && isSegmented && (
          <div className='d-flex ms-2'>
            <Button
              variant='primary'
              className='button-medium d-flex align-items-center justify-content-center me-1'
              onClick={onPrevious}
              disabled={!canPrevious || isLoadingSave || isLoadingContactChange}
            >
              <i className='bi bi-chevron-left' />
            </Button>
            <Button
              variant='primary'
              className='button-medium d-flex align-items-center justify-content-center'
              onClick={onNext}
              disabled={!canNext || isLoadingSave || isLoadingContactChange}
            >
              <i className='bi bi-chevron-right' />
            </Button>
          </div>
        )}
      </div>

      <ConfirmationModal />
    </div>
  )
}
